import { useEffect, useRef, useState, type ImgHTMLAttributes, type ReactEventHandler } from 'react';

interface RetryImageProps extends ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  maxRetries?: number;
  retryDelayMs?: number;
}

/**
 * <img> that retries on load failure instead of showing the browser's
 * permanent broken-image icon. Covers transient failures — a network blip on
 * the viewer's connection, or an intermediate cache (mobile carrier proxy,
 * browser HTTP cache) having served a stale error — by refetching with a
 * cache-busting query param on each attempt. Not a fix for a genuinely dead
 * URL (an old job whose source has expired): after retries are exhausted it
 * shows a manual "Tải lại" button instead of retrying forever.
 */
export function RetryImage({ src, maxRetries = 3, retryDelayMs = 1000, onError, ...rest }: RetryImageProps) {
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup runs BEFORE this effect re-fires on a src change, and on unmount —
  // covers both cases in one place, so a retry timer scheduled for the
  // PREVIOUS src can never fire after the caller has already moved on to a
  // new src (it would otherwise bump the new image's attempt count and force
  // an unnecessary refetch of an image that was loading fine).
  useEffect(() => {
    setAttempt(0);
    setFailed(false);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [src]);

  const bustedSrc = attempt === 0 ? src : `${src}${src.includes('?') ? '&' : '?'}retry=${attempt}`;

  const handleError: ReactEventHandler<HTMLImageElement> = (e) => {
    if (attempt < maxRetries) {
      timeoutRef.current = setTimeout(() => setAttempt((a) => a + 1), retryDelayMs * (attempt + 1));
    } else {
      setFailed(true);
    }
    onError?.(e);
  };

  if (failed) {
    // A <div role="button">, not a real <button> — callers may already sit
    // inside a <button> (Recent Portraits strip on the preview page) or an
    // <a href> (Recent Portraits strip on the landing page), and a nested
    // <button> is invalid HTML while a plain click inside an <a> would still
    // navigate. preventDefault() + stopPropagation() makes this safe and
    // inert in either parent, so "Try Again" only ever retries in place.
    const retry = () => {
      setFailed(false);
      setAttempt(0);
    };
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          retry();
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            retry();
          }
        }}
        className="w-full h-full flex flex-col items-center justify-center gap-1 bg-cream2 text-warm-brown/60 cursor-pointer"
        style={{ fontSize: 11 }}
        aria-label="Try Again"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M21 12a9 9 0 1 1-2.64-6.36" />
          <path d="M21 3v6h-6" />
        </svg>
        Try Again
      </div>
    );
  }

  return <img src={bustedSrc} onError={handleError} {...rest} />;
}
